import type { DiffStat, FileChange, Snapshot, SnapshotFileSummary, SnapshotSummary } from "./models.ts";

/**
 * Snapshot summaries (K3). The API computes one at upload and the daemon's
 * LocalApi computes the same shape off disk; both go through here so a
 * `diffHash` or a total can never mean two different things.
 */

/** Totals over a list of changes: one file per entry. */
export function diffStatOfChanges(changes: readonly Pick<FileChange, "additions" | "deletions">[]): DiffStat {
  let additions = 0;
  let deletions = 0;
  for (const change of changes) {
    additions += change.additions;
    deletions += change.deletions;
  }
  return { files: changes.length, additions, deletions };
}

/** A worktree's `diffStat`: the branch totals when the snapshot has them,
 * otherwise the uncommitted totals. Never their sum — `branchChanges`
 * already includes `changes`. */
export function diffStatFromSummary(summary: Pick<SnapshotSummary, "totals" | "branchTotals">): DiffStat {
  return summary.branchTotals ?? summary.totals;
}

const encoder = new TextEncoder();

export function utf8ByteLength(text: string): number {
  return encoder.encode(text).byteLength;
}

/** Lowercase sha-256 hex of the text's UTF-8 bytes (WebCrypto: Workers, Bun
 * and browsers). */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", encoder.encode(text));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** `diffHash` for a served diff: sha-256 hex, or `""` when there is none. */
export async function diffHashOf(diff: string | null | undefined): Promise<string> {
  return diff === undefined || diff === null ? "" : sha256Hex(diff);
}

/** A file change without its diff text. `diffBytes` is the worker-reported
 * original size when it capped the diff, else the served diff's own size. */
export async function summarizeFileChange(change: FileChange): Promise<SnapshotFileSummary> {
  const { diff, diffBytes, diffTruncated, ...rest } = change;
  return {
    ...rest,
    diffBytes: diffBytes ?? (diff === undefined ? 0 : utf8ByteLength(diff)),
    diffHash: await diffHashOf(diff),
    ...(diffTruncated === undefined ? {} : { diffTruncated }),
  };
}

/** The summary of a full snapshot: no diff text, no path list. */
export async function summarizeSnapshot(snapshot: Snapshot): Promise<SnapshotSummary> {
  const changes = await Promise.all(snapshot.changes.map(summarizeFileChange));
  const branchChanges = snapshot.branchChanges === undefined
    ? undefined
    : await Promise.all(snapshot.branchChanges.map(summarizeFileChange));
  return {
    id: snapshot.id,
    worktreeId: snapshot.worktreeId,
    branch: snapshot.branch,
    ahead: snapshot.ahead,
    behind: snapshot.behind,
    changes,
    ...(branchChanges === undefined ? {} : { branchChanges }),
    commits: snapshot.commits,
    createdAt: snapshot.createdAt,
    totals: diffStatOfChanges(snapshot.changes),
    ...(snapshot.branchChanges === undefined ? {} : { branchTotals: diffStatOfChanges(snapshot.branchChanges) }),
    pathsCount: snapshot.paths.length,
  };
}
